import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { CalendarRange, Sparkles, Loader2, Check, X, Trash2, ChevronDown, ChevronUp, Images } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WeeklyPlannerPage() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [weekStart, setWeekStart] = useState("");
  const [generating, setGenerating] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);
  const [autoFillResult, setAutoFillResult] = useState(null);
  const [plans, setPlans] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => setCompanies(r.data)).catch(console.error);
    loadPlans();
    // Default to next Monday
    const d = new Date();
    const diff = (8 - d.getDay()) % 7 || 7;
    d.setDate(d.getDate() + diff);
    setWeekStart(d.toISOString().split("T")[0]);
  }, []);

  const loadPlans = async () => {
    try {
      const res = await axios.get(`${API}/weekly-plans`, { withCredentials: true });
      setPlans(res.data);
    } catch (e) { console.error(e); }
  };

  const handleGenerate = async () => {
    if (!selectedCompany || !weekStart) {
      toast.error("Bitte Firma und Startdatum waehlen");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/weekly-plans/generate`, {
        company_id: selectedCompany,
        week_start: weekStart,
      }, { withCredentials: true });
      toast.success("Wochenplan generiert!");
      loadPlans();
      setExpanded(res.data.id);
    } catch (e) {
      toast.error("Fehler bei der Generierung");
      console.error(e);
    }
    setGenerating(false);
  };

  const handleAutoFillFromMedia = async () => {
    if (!selectedCompany || !weekStart) {
      toast.error("Bitte Firma und Startdatum waehlen");
      return;
    }
    setAutoFilling(true);
    setAutoFillResult(null);
    try {
      const res = await axios.post(`${API}/companies/${selectedCompany}/auto-fill-week`, {
        week_start: weekStart,
      }, { withCredentials: true });
      setAutoFillResult(res.data);
      toast.success(res.data?.message || "Woche gefuellt");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-Fill fehlgeschlagen");
    }
    setAutoFilling(false);
  };

  const updateItemStatus = async (planId, itemIndex, status) => {
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;
    const items = [...plan.items];
    items[itemIndex] = { ...items[itemIndex], status };
    try {
      await axios.put(`${API}/weekly-plans/${planId}`, { items }, { withCredentials: true });
      toast.success("Status aktualisiert");
      loadPlans();
    } catch { toast.error("Fehler"); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Wochenplan loeschen?")) return;
    try {
      await axios.delete(`${API}/weekly-plans/${id}`, { withCredentials: true });
      toast.success("Plan geloescht");
      loadPlans();
    } catch { toast.error("Fehler"); }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "Unbekannt";

  const itemStatusColors = {
    suggested: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    created: "bg-blue-100 text-blue-800",
  };

  const platformColors = {
    instagram: "bg-pink-100 text-pink-700",
    facebook: "bg-blue-100 text-blue-700",
    tiktok: "bg-slate-900 text-white",
    linkedin: "bg-sky-100 text-sky-800",
    pinterest: "bg-red-100 text-red-700",
  };
  const platformBorder = {
    instagram: "#DB2777",
    facebook: "#1877F2",
    tiktok: "#0F172A",
    linkedin: "#0A66C2",
    pinterest: "#E60023",
  };

  const itemStatusLabels = {
    suggested: "Vorgeschlagen",
    approved: "Genehmigt",
    rejected: "Abgelehnt",
    created: "Erstellt",
  };

  return (
    <div data-testid="weekly-planner-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight">{t("planner.title")}</h1>
        <p className="text-[var(--studio-text-muted)] mt-1">Plane und verwalte den Content einer Firma fuer die gesamte Woche — hier siehst du, was auf welcher Plattform an welchem Tag veroeffentlicht werden soll.</p>
      </div>

      {/* Generator */}
      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader>
          <CardTitle className="font-heading text-base">Neuen Wochenplan generieren</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label className="text-xs text-[var(--studio-text-muted)]">Firma</Label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger className="mt-1" data-testid="planner-company-select">
                  <SelectValue placeholder="Firma waehlen" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[var(--studio-text-muted)]">Woche ab</Label>
              <Input
                type="date"
                value={weekStart}
                onChange={(e) => setWeekStart(e.target.value)}
                className="mt-1"
                data-testid="planner-week-start"
              />
            </div>
            <Button
              onClick={handleGenerate}
              disabled={generating || !selectedCompany}
              data-testid="generate-plan-btn"
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-10"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? "Wird generiert..." : "Plan generieren"}
            </Button>
            <Button
              onClick={handleAutoFillFromMedia}
              disabled={autoFilling || !selectedCompany || !weekStart}
              data-testid="auto-fill-week-btn"
              variant="outline"
              className="h-10 border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/10"
              title="Smart-Befuellung der Woche aus unverwendeten Medien"
            >
              {autoFilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Images className="w-4 h-4 mr-2" />}
              {autoFilling ? "Fuelle Woche..." : "Woche aus Medien fuellen"}
            </Button>
          </div>
          {autoFillResult && (
            <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="auto-fill-result">
              <p className="text-xs font-medium text-emerald-900">{autoFillResult.message}</p>
              {autoFillResult.created?.length > 0 && (
                <ul className="mt-2 space-y-0.5 max-h-48 overflow-y-auto">
                  {autoFillResult.created.map((c) => (
                    <li key={c.content_id} className="text-[11px] text-emerald-900/80">
                      <b>{c.day_label}</b> {c.schedule_time} · <span className="font-mono">{c.format_type}</span> · {c.caption_preview.substring(0, 70)}…
                    </li>
                  ))}
                </ul>
              )}
              {autoFillResult.skipped?.length > 0 && (
                <p className="text-[11px] text-amber-700 mt-1">{autoFillResult.skipped.length} uebersprungen (KI-Fehler)</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plans */}
      {plans.length === 0 ? (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardContent className="py-16 text-center">
            <CalendarRange className="w-12 h-12 mx-auto text-[var(--studio-text-muted)] opacity-40 mb-4" />
            <p className="text-[var(--studio-text-muted)]">Noch keine Wochenplaene erstellt.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {plans.map((plan) => (
            <Card key={plan.id} className="border-[var(--studio-border)] shadow-none" data-testid={`plan-${plan.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-medium font-heading">{companyName(plan.company_id)}</p>
                      <p className="text-xs text-[var(--studio-text-muted)]">
                        Woche ab {plan.week_start} &middot; {plan.items?.length || 0} Eintraege
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setExpanded(expanded === plan.id ? null : plan.id)}>
                      {expanded === plan.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deletePlan(plan.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                {expanded === plan.id && plan.items?.length > 0 && (
                  <div className="space-y-2 pt-3 border-t border-[var(--studio-border)] animate-fade-in">
                    {plan.items.map((item, i) => (
                      <div key={i} className="flex items-start justify-between p-3 rounded-lg bg-[var(--studio-bg)]" style={{ borderLeft: `3px solid ${platformBorder[item.platform] || "#94A3B8"}` }} data-testid={`plan-item-${i}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">{item.day}</span>
                            <span className="text-xs text-[var(--studio-text-muted)]">{item.date}</span>
                            <Badge variant="outline" className="text-[10px]">{item.type}</Badge>
                            <Badge className={`${platformColors[item.platform] || "bg-gray-100 text-gray-700"} border-0 text-[10px] capitalize`}>{item.platform}</Badge>
                            <Badge className={`${itemStatusColors[item.status] || itemStatusColors.suggested} border-0 text-[10px]`}>
                              {itemStatusLabels[item.status] || item.status}
                            </Badge>
                          </div>
                          <p className="text-sm">{item.idea}</p>
                          {item.caption_hint && (
                            <p className="text-xs text-[var(--studio-text-muted)] mt-1 italic">"{item.caption_hint}"</p>
                          )}
                          <p className="text-xs text-[var(--studio-text-muted)] mt-1">
                            Ziel: {item.goal} &middot; {item.suggested_time}
                          </p>
                        </div>
                        {item.status === "suggested" && (
                          <div className="flex gap-1 ml-3">
                            <Button size="sm" variant="ghost" onClick={() => updateItemStatus(plan.id, i, "approved")}
                              className="h-7 w-7 p-0 text-green-600 hover:bg-green-50" data-testid={`approve-item-${i}`}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => updateItemStatus(plan.id, i, "rejected")}
                              className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid={`reject-item-${i}`}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
